<template>
  <div
    v-click-outside="onClickOutside"
    :class="{ 'search-form__loading': showSpinner, 'search-form__error': error || serverError }"
    class="search-form"
    @mousedown.stop
  >
    <div class="input-wrapper" :class="{ 'without-search-icon': inputValue.length > 0 || error }">
      <input
        ref="inputValue"
        v-model="inputValue"
        :placeholder="placeholder"
        type="text"
        :disabled="disable"
        @keyup="onKeyUp($event)"
      />
      <div v-if="inputValue.length > 0 && !serverError" class="upper-label">{{ label }}</div>
      <div v-if="serverError" class="upper-label upper-label__error">{{ errorLabel }}</div>
      <circle-loader v-if="showSpinner" size="small" :indeterminate="true" />
      <button v-if="inputValue.length > 0" type="button" class="reset-input-value" @click.stop="onReset" />
    </div>

    <div :class="{ 'input-info__error': error }" class="input-info">{{ bottomInfo }}</div>
    <div class="error-input-info">{{ errorInfo }}</div>

    <div v-if="showSuggestions" class="suggestions">
      <ul>
        <slot name="suggestion-item" :suggestions="suggestions" :on-click-suggestion="suggest => onClickSuggestion(suggest)">
          <li v-if="suggestions.length === 0">Нет данных</li>
          <li
            v-for="(suggest, index) in suggestions"
            :key="index"
            :class="{ 'active-suggest': suggest.lastChecked }"
            @click.stop="onClickSuggestion(suggest)"
          >
            <span class="suggestion-element-top">
              {{ suggest.name }}
            </span>
            <span v-if="suggest.code" class="suggestion-element-bottom">
              {{ suggest.code }}
            </span>
          </li>
        </slot>
      </ul>
    </div>
  </div>
</template>

<script>
import CircleLoader from "../../../../components/CircleLoader";

export default {
  name: "SearchForm",
  components: {
    CircleLoader
  },
  props: {
    inputModel: {
      type: Object,
      default: () => ({})
    },
    error: {
      type: Boolean,
      default: false
    },
    serverError: {
      type: Boolean,
      default: false
    },
    disable: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ""
    },
    placeholder: {
      type: String,
      default: ""
    },
    suggestFieldType: {
      type: String,
      default: ""
    },
    getData: {
      type: Function,
      default: () => {}
    },
    bottomInfo: {
      type: String,
      default: ""
    },
    errorInfo: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      inputValue: "",
      showSuggestions: false,
      keyUpInterval: null,
      suggestions: [],
      showSpinner: false
    };
  },
  mounted() {
    if (this.inputModel !== null) {
      this.inputValue = this.inputModel[this.suggestFieldType];
    }
  },
  methods: {
    onKeyUp(event) {
      const value = this.inputValue;

      // 91 - left cmd key; 93 - right cmd key; 37 - left arrow
      if (event.keyCode === 91 || event.keyCode === 93 || event.keyCode === 37) return;

      // 40 - down arrow; 38 - up arrow
      if (event.keyCode === 40 || event.keyCode === 38) {
        if (this.suggestions.length > 0) {
          let lastIndex = this.suggestions.findIndex(s => s.lastChecked);
          if (lastIndex > -1) {
            if (lastIndex > this.suggestions.length && event.keyCode === 40) {
              lastIndex = 0;
            }
            if (lastIndex === 0 && event.keyCode === 38) {
              lastIndex = this.suggestions.length;
            }
            const action = event.keyCode === 38 ? lastIndex - 1 : lastIndex + 1;
            this.suggestions = this.suggestions.map((s, index) => {
              if (index === action) {
                s.lastChecked = true;
                return s;
              }
              s.lastChecked = false;
              return s;
            });
          } else {
            const active = event.keyCode === 38 ? this.suggestions.length - 1 : 0;
            this.suggestions = this.suggestions.map((s, index) => {
              if (index === active) {
                s.lastChecked = true;
                return s;
              }
              s.lastChecked = false;
              return s;
            });
          }
        }
        return;
      }

      // 39 - right arrow; 13 - enter
      if (event.keyCode === 39 || event.keyCode === 13) {
        const lastIndex = this.suggestions.findIndex(s => s.lastChecked);
        if (lastIndex > -1) {
          this.onClickSuggestion(this.suggestions[lastIndex]);
        }

        if (event.keyCode === 13) {
          this.showSuggestions = false;
        }
        return;
      }

      clearInterval(this.keyUpInterval);
      this.keyUpInterval = setInterval(() => {
        if (value.length > 0 && value.length < 500) {
          this.showSpinner = true;

          this.getData(value).then(data => {
            this.showSpinner = false;
            this.suggestions = data;
            this.showSuggestions = true;
          });
        }
        clearInterval(this.keyUpInterval);
        this.keyUpInterval = null;
      }, 300);

      if (value.length === 0) {
        this.showSuggestions = false;
        clearInterval(this.keyUpInterval);
        this.keyUpInterval = null;
      }
    },
    onClickSuggestion(suggest) {
      // this.showSuggestions = false;
      // this.inputValue = suggest[this.suggestFieldType];
      // this.bottomInfo = suggest["code"];
      // this.clearInputValue();
      this.$emit("select-suggestion", { element: this, suggest });
    },
    onReset() {
      clearInterval(this.keyUpInterval);
      this.keyUpInterval = null;
      this.showSuggestions = false;
      this.$emit("reset", this);
    },
    onClickOutside() {
      this.$emit("blur", this);
    }
  }
};
</script>

<style lang="postcss" scoped>
.search-form {
  position: relative;
  margin: 0 0 32px;

  .input-wrapper {
    position: relative;

    &::after {
      content: "";
      position: absolute;
      width: 24px;
      height: 24px;
      background: svg-load("../../../../assets/images/ui-icons-24/search.svg", fill=#3586FF) no-repeat center/24px;
      bottom: 8px;
      right: 16px;
    }

    &.without-search-icon {
      &::after {
        content: none;
      }
    }
  }

  input[type="text"] {
    background-color: #f0f3fa;
    border-radius: 4px 4px 0 0;
    width: 100%;
    height: 56px;
    font-size: 16px;
    border: none;
    box-shadow: inset 0 -1px rgba(0, 0, 0, 0.1);
    line-height: 24px;
    padding-top: 15px;
    padding: 15px 56px 0 12px;

    &:hover {
      box-shadow: inset 0 -1px #5c9dff;
    }

    &:focus {
      outline: none;
      box-shadow: inset 0 -1px #5c9dff;
    }

    &::placeholder {
      color: #000;
      opacity: 0.5;
    }

    &:focus {
      outline: none;
    }
  }

  .reset-input-value {
    cursor: pointer;
    background: svg-load("../../../../assets/images/ui-icons-24/circle-with-cross.svg", fill=#3061AA) no-repeat center/24px;
    width: 24px;
    height: 24px;
    opacity: 0.23;
    border: none;
    padding: 0;
    position: absolute;
    right: 16px;
    bottom: 8px;
    outline: none;
    z-index: 2;

    &:focus {
      outline: none;
    }
  }

  .input-info {
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.5px;
    color: #3586ff;
    padding-left: 16px;
    padding-top: 4px;

    &:empty {
      display: none;
    }

    &.input-info__error {
      color: #f23047;
      opacity: 1;
    }
  }

  .error-input-info {
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 0.5px;
    position: absolute;
    color: #f23047;
    padding-left: 12px;
    padding-top: 0;
    display: none;
  }

  .upper-label {
    position: absolute;
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 0.5px;
    opacity: 0.4;
    color: #000;
    top: 5px;
    left: 12px;
    z-index: 1;
    pointer-events: none;

    &.upper-label__error {
      color: #f23047;
      opacity: 1;
    }
  }

  .suggestions {
    position: absolute;
    background-color: #fff;
    z-index: 1000;
    left: 0;
    right: 0;
    top: calc(100% + 8px);
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

    ul {
      list-style: none;
      padding-left: 0;
      margin-bottom: 0;

      li {
        padding: 12px 16px;
        cursor: pointer;
        color: #000;

        + li {
          border-top: 1px solid rgba(92, 157, 255, 0.3);
        }

        &:hover {
          background-color: #f0f3fa;

          .suggestion-element-top {
            color: #3586ff;
          }
        }

        &.active-suggest {
          background-color: #f0f3fa;
        }

        &:first-child {
          border-top-right-radius: 4px;
          border-top-left-radius: 4px;
        }

        &:last-child {
          border-bottom-right-radius: 4px;
          border-bottom-left-radius: 4px;
        }

        .suggestion-element-top {
          display: block;
          font-size: 16px;
          line-height: 24px;
          margin-bottom: 4px;
          letter-spacing: 0.4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          color: #333;
        }

        .suggestion-element-bottom {
          display: inline-block;
          font-size: 12px;
          line-height: 16px;
          color: #3586ff;
          letter-spacing: 0.5px;
          background: rgba(53, 134, 255, 0.2);
          padding: 0 0.25em;
        }
      }
    }
  }

  &__error {
    .close-form-data {
      display: none;
    }

    .mx-datepicker .mx-input-append {
      display: none;
    }

    input[type="text"] {
      box-shadow: inset 0 -1px #f23047;

      &:hover,
      &:active {
        box-shadow: inset 0 -1px #f23047;
      }

      &:focus {
        box-shadow: inset 0 -1px #f23047;
      }
    }

    .error-input-info {
      display: block;
    }

    .upper-label {
      color: #f23047;
      opacity: 1;
    }
  }

  &__loading {
    .input-wrapper {
      .circle-loader {
        position: absolute;
        right: 47px;
        bottom: 12px;
      }
    }
  }
}
</style>
